import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { PageHeader } from "@/widgets/page-header"
import { useEffect, useState, useMemo } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import * as yup from 'yup'
import { 
    fetchOrganizationProfile, 
    fetchUpdateContactPerson, 
    fetchSubmitForModeration,
    selectOnboardingOrganization,
    selectOnboardingContact,
    selectOnboardingLoading,
    selectOnboardingError
} from "@/entities/onboarding"
import { Loader } from "@/shared/ui/loader"
import { OrganizationData } from "./organization-data"
import { ContactPerson } from "./contact-person"
import { ErrorAlert, SuccessAlert } from "./alerts"
import notify from "@/shared/lib/notify"

type ContactForm = {
    full_name: string
    position: string
    phone: string
    email: string
}

const contactSchema = yup.object({
    full_name: yup
        .string()
        .required("Введите ФИО")
        .min(3, "ФИО должно содержать минимум 3 символа"),
    position: yup
        .string()
        .required("Введите должность")
        .min(2, "Должность должна содержать минимум 2 символа"),
    phone: yup
        .string()
        .required("Введите телефон"),
    email: yup
        .string()
        .required("Введите email")
        .email("Введите корректный email")
})

export const CompanyProfilePage = () => {
    const dispatch = useAppDispatch()
    const organization = useAppSelector(selectOnboardingOrganization)
    const contact = useAppSelector(selectOnboardingContact)
    const isLoading = useAppSelector(selectOnboardingLoading)
    const error = useAppSelector(selectOnboardingError)
    const [isSaving, setIsSaving] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { 
        register, 
        handleSubmit, 
        setValue, 
        watch, 
        formState: { errors } 
    } = useForm<ContactForm>({
        resolver: yupResolver(contactSchema) as any,
        defaultValues: {
            full_name: '',
            position: '',
            phone: '',
            email: ''
        }
    })

    const formValues = watch()

    useEffect(() => {
        dispatch(fetchOrganizationProfile())
    }, [dispatch])

    useEffect(() => {
        if (contact) {
            setValue('full_name', contact.full_name || '')
            setValue('position', contact.position || '')
            setValue('phone', contact.phone || '')
            setValue('email', contact.email || '')
        }
    }, [contact, setValue])

    const onSubmit: SubmitHandler<ContactForm> = async (data) => {
        setIsSaving(true)
        try {
            await dispatch(fetchUpdateContactPerson(data)).unwrap()
            notify.success('Контактное лицо обновлено')
        } catch (error) {
            console.error('Ошибка при сохранении контакта:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleSubmitForModeration = async () => {
        setIsSubmitting(true)
        try {
            await dispatch(fetchUpdateContactPerson({
                full_name: formValues.full_name,
                position: formValues.position,
                phone: formValues.phone,
                email: formValues.email
            })).unwrap()

            await dispatch(fetchSubmitForModeration()).unwrap()
            notify.success('Спасибо! Данные переданы на проверку. Мы свяжемся с указанным контактным лицом.')
        } catch (error) {
            console.error('Ошибка при отправке на модерацию:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const isContactComplete = useMemo(() => {
        return Boolean(
            formValues.full_name?.trim() &&
            formValues.position?.trim() &&
            formValues.phone?.trim() &&
            formValues.email?.trim()
        )
    }, [formValues])

    const canSubmit = useMemo(() => {
        return organization?.verification_status === 'draft' || 
               organization?.verification_status === 'rejected'
    }, [organization?.verification_status])

    if (isLoading && !organization && !contact) {
        return (
            <div className="_page_container">
                <PageHeader title="Профиль организации" />
                <div className="flex justify-center items-center min-h-[400px]">
                    <Loader />
                </div>
            </div>
        )
    }

    return (
        <div className="_page_container">
            <PageHeader title="Профиль организации" />
            
            {error && <ErrorAlert error={error} />}

            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                    <OrganizationData organization={organization} />
                </div>

                <div className="col-span-12">
                    <ContactPerson
                        register={register}
                        errors={errors}
                        isSaving={isSaving}
                        isSubmitting={isSubmitting}
                        isContactComplete={isContactComplete}
                        canSubmit={canSubmit}
                        onSubmit={handleSubmit(onSubmit)}
                        onSubmitForModeration={handleSubmitForModeration}
                    />
                </div>
            </div>
        </div>
    )
}
